import type { AutomationTriggerType } from '@adweb/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Star } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { OrganizationFilter } from '../../components/OrganizationFilter';
import { ActionsMenu, type ActionsMenuItem } from '../../components/ui/ActionsMenu';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { TriggerTypeBadge } from '../../components/ui/TriggerTypeBadge';
import { apiClient } from '../../lib/api-client';
import { useAuth } from '../auth/auth-context';
import { useUiPreferences } from '../preferences/ui-preferences';
import { RequestAutomationModal } from '../requests/RequestAutomationModal';

import type { AutomationSummary } from './automation-summary';
import { AutomationActionButton } from './AutomationActionButton';
import { AutomationTiles } from './AutomationTiles';
import { CreateAutomationModal } from './CreateAutomationModal';
import { EditAutomationModal } from './EditAutomationModal';
import { FavoriteStar, favoritesFirst, matchesSearch, useFavorites } from './favorites';
import { RunAutomationModal } from './RunAutomationModal';
import { ViewToggle } from './ViewToggle';

type Automation = AutomationSummary;

interface ListResponse {
  data: Automation[];
  meta: { total: number };
}

async function fetchAutomations(organizationId: string): Promise<ListResponse> {
  const res = await apiClient.get<ListResponse>('/automations', {
    params: { page: 1, pageSize: 100, ...(organizationId ? { organizationId } : {}) },
  });
  return res.data;
}

interface IncidentDot {
  automationId: string;
  open: number;
}

async function fetchOpenIncidents(): Promise<IncidentDot[]> {
  const res = await apiClient.get<{
    data: Array<{ automationId: string }>;
  }>('/incidents', { params: { status: 'OPEN', page: 1, pageSize: 100 } });
  const map = new Map<string, number>();
  for (const i of res.data.data) {
    map.set(i.automationId, (map.get(i.automationId) ?? 0) + 1);
  }
  return Array.from(map, ([automationId, open]) => ({ automationId, open }));
}

function HealthDot({ open }: { open: number }) {
  if (open === 0) {
    return (
      <span
        title="Aucun incident ouvert"
        className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500"
      />
    );
  }
  if (open < 3) {
    return (
      <span
        title={`${open} incident(s) ouvert(s)`}
        className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500"
      />
    );
  }
  return (
    <span
      title={`${open} incident(s) ouvert(s)`}
      className="inline-block h-2.5 w-2.5 rounded-full bg-red-500"
    />
  );
}

const TYPE_FILTERS: Array<{ value: AutomationTriggerType | 'ALL'; label: string }> = [
  { value: 'ALL', label: 'Tous' },
  { value: 'FORM', label: 'Formulaire' },
  { value: 'SCHEDULED', label: 'Planifié' },
  { value: 'WEBHOOK', label: 'Webhook' },
  { value: 'CHAT', label: 'Chat' },
  { value: 'EMAIL', label: 'E-mail' },
];

export function AutomationsListPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<AutomationTriggerType | 'ALL'>('ALL');
  const [orgFilter, setOrgFilter] = useState('');
  const [search, setSearch] = useState('');
  const [requestOpen, setRequestOpen] = useState(false);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const { favorites, toggle: toggleFavorite, pending: favoritePending } = useFavorites();
  const [runTarget, setRunTarget] = useState<Automation | null>(null);
  const { automationView, tileColumns } = useUiPreferences();
  const canCreate = user?.role === 'SUPER_ADMIN' || user?.role === 'ADWEB_OPERATOR';
  const canDelete = user?.role === 'SUPER_ADMIN';
  const isPlatform = user?.role === 'SUPER_ADMIN' || user?.role === 'ADWEB_OPERATOR';
  // SUPER_ADMIN + ADWEB_OPERATOR partout ; CLIENT_ADMIN uniquement sur sa propre org.
  const canToggleStatus = (a: { organizationId?: string }) => {
    if (user?.role === 'SUPER_ADMIN' || user?.role === 'ADWEB_OPERATOR') return true;
    if (user?.role === 'CLIENT_ADMIN' && a.organizationId === user.organizationId) return true;
    return false;
  };
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['automations', 'list', orgFilter],
    queryFn: () => fetchAutomations(orgFilter),
    placeholderData: keepPreviousData,
    refetchInterval: 10_000,
  });
  const incidents = useQuery({
    // Clé distincte de la vue Dashboard (['incidents','open']) qui renvoie une
    // autre forme de données (réponse brute) — sinon collision de cache → crash.
    queryKey: ['incidents', 'open', 'dots'],
    queryFn: fetchOpenIncidents,
    refetchInterval: 10_000,
    enabled: isPlatform, // API restreinte aux rôles plateforme
  });

  // Garde défensive : ne mappe que si on a bien un tableau.
  const incidentDots = Array.isArray(incidents.data) ? incidents.data : [];
  const incidentByAuto = new Map(incidentDots.map((i) => [i.automationId, i.open]));

  const rows = favoritesFirst(
    (list.data?.data ?? []).filter(
      (a) =>
        (typeFilter === 'ALL' || a.triggerType === typeFilter) &&
        (!favoritesOnly || favorites.has(a.id)) &&
        matchesSearch(a, search),
    ),
    favorites,
  );
  const filtered = typeFilter !== 'ALL' || !!orgFilter || favoritesOnly || !!search.trim();

  const del = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/automations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automations'] }),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'ACTIVE' | 'PAUSED' }) =>
      apiClient.post(`/automations/${id}/set-status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automations'] }),
  });

  function handleToggleStatus(a: Automation) {
    const next: 'ACTIVE' | 'PAUSED' = a.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    const verb = next === 'PAUSED' ? 'désactiver' : 'réactiver';
    if (window.confirm(`Voulez-vous ${verb} l'automatisation « ${a.name} » ?`)) {
      setStatus.mutate({ id: a.id, status: next });
    }
  }

  /** Actions secondaires (menu « ⋯ ») selon le rôle : liste et tuiles. */
  function actionItems(a: Automation): ActionsMenuItem[] {
    const items: ActionsMenuItem[] = [];
    if (canToggleStatus(a) && (a.status === 'ACTIVE' || a.status === 'PAUSED')) {
      items.push({
        label: a.status === 'ACTIVE' ? 'Désactiver' : 'Réactiver',
        onSelect: () => handleToggleStatus(a),
        disabled: setStatus.isPending,
        hint:
          a.status === 'ACTIVE'
            ? "Mettre en pause : l'automatisation ne sera plus déclenchable jusqu'à réactivation"
            : "Réactiver : l'automatisation redevient déclenchable",
      });
    }
    if (canCreate) items.push({ label: 'Modifier', onSelect: () => setEditId(a.id) });
    if (canDelete) {
      items.push({
        label: 'Supprimer',
        onSelect: () => handleDelete(a),
        danger: true,
        disabled: del.isPending,
      });
    }
    return items;
  }

  function handleDelete(a: Automation) {
    if (
      window.confirm(
        `Supprimer définitivement « ${a.name} » ?\n\nCela efface aussi tout son historique ` +
          '(exécutions, incidents, KPIs). Action irréversible.',
      )
    ) {
      del.mutate(a.id);
    }
  }

  return (
    <>
      <PageHeader
        helpTopic="lancer"
        title={t('automations.title')}
        subtitle={t('automations.subtitle')}
        actions={
          <>
            <ViewToggle inHeader />
            {!isPlatform && (
              <Button variant="inverse" onClick={() => setRequestOpen(true)} size="sm">
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                Demander une automatisation
              </Button>
            )}
            {canCreate && (
              <Button variant="inverse" onClick={() => setModalOpen(true)} size="sm">
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                Référencer une automatisation
              </Button>
            )}
          </>
        }
      />
      <RunAutomationModal
        open={!!runTarget}
        onClose={() => setRunTarget(null)}
        automationId={runTarget?.id ?? ''}
        automationName={runTarget?.name ?? ''}
        fields={runTarget?.paramsSchema?.fields ?? []}
      />
      <CreateAutomationModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <RequestAutomationModal open={requestOpen} onClose={() => setRequestOpen(false)} />
      <EditAutomationModal open={!!editId} onClose={() => setEditId(null)} automationId={editId} />
      <section className="px-4 py-6 sm:px-6 lg:px-8">
        <div
          className="mb-4 flex flex-wrap items-center gap-2"
          role="group"
          aria-label="Filtrer par type"
        >
          <div className="relative">
            <label htmlFor="automations-search" className="sr-only">
              Rechercher une automatisation
            </label>
            <Search
              className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-slate-400"
              aria-hidden="true"
            />
            <input
              id="automations-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher…"
              className="w-56 rounded-lg border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            />
          </div>
          {isPlatform && <OrganizationFilter value={orgFilter} onChange={setOrgFilter} />}
          <button
            type="button"
            onClick={() => setFavoritesOnly((v) => !v)}
            aria-pressed={favoritesOnly}
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium transition ${
              favoritesOnly
                ? 'bg-amber-500 text-white'
                : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50'
            }`}
          >
            <Star
              className={`h-3.5 w-3.5 ${favoritesOnly ? 'fill-current' : ''}`}
              aria-hidden="true"
            />
            Favoris
          </button>
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setTypeFilter(f.value)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                typeFilter === f.value
                  ? 'bg-brand text-white'
                  : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {automationView === 'TILES' && (
          <>
            <AutomationTiles
              automations={rows}
              showOrganization={isPlatform}
              onRun={setRunTarget}
              columns={tileColumns}
              favorites={favorites}
              onToggleFavorite={toggleFavorite}
              actions={actionItems}
            />
            {list.data && rows.length === 0 && (
              <p className="surface px-4 py-8 text-center text-sm text-slate-400">
                {!filtered ? t('automations.empty') : t('automations.emptyFiltered')}
              </p>
            )}
          </>
        )}
        {automationView === 'LIST' && (
          <div className="overflow-x-auto surface">
            {/* Petits écrans : seules les colonnes utiles restent (nom, lancement, actions). */}
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 [&_th]:whitespace-nowrap">
                <tr>
                  <th scope="col" className="w-8 px-4 py-3" aria-label="Santé"></th>
                  <th scope="col" className="px-4 py-3">
                    {t('automations.name')}
                  </th>
                  {isPlatform && (
                    <th scope="col" className="hidden px-4 py-3 xl:table-cell">
                      {t('app.organization')}
                    </th>
                  )}
                  <th scope="col" className="hidden px-4 py-3 2xl:table-cell">
                    {t('automations.category')}
                  </th>
                  <th scope="col" className="hidden px-4 py-3 sm:table-cell">
                    {t('automations.type')}
                  </th>
                  <th scope="col" className="hidden px-4 py-3 md:table-cell">
                    {t('automations.status')}
                  </th>
                  <th scope="col" className="hidden px-4 py-3 xl:table-cell">
                    {t('automations.gainPerRun')}
                  </th>
                  <th scope="col" className="hidden px-4 py-3 text-right sm:table-cell">
                    {t('automations.action')}
                  </th>
                  <th scope="col" className="px-4 py-3" aria-label={t('app.actions')}></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50">
                    <td className="py-3 pl-4 pr-1 sm:px-4">
                      <HealthDot open={incidentByAuto.get(a.id) ?? 0} />
                    </td>
                    <td className="px-2 py-3 sm:px-4">
                      <div className="flex items-start gap-1.5">
                        <FavoriteStar
                          automation={a}
                          favorite={favorites.has(a.id)}
                          onToggle={toggleFavorite}
                          disabled={favoritePending}
                        />
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900">{a.name}</div>
                          <div className="hidden text-xs text-slate-500 sm:block">{a.slug}</div>
                          {/* Téléphone : le bouton de lancement passe sous le nom. */}
                          <div className="mt-2 sm:hidden">
                            <AutomationActionButton automation={a} onRun={setRunTarget} />
                          </div>
                        </div>
                      </div>
                    </td>
                    {isPlatform && (
                      <td className="hidden px-4 py-3 text-slate-600 xl:table-cell">
                        {a.organization?.name ?? '—'}
                      </td>
                    )}
                    <td className="hidden px-4 py-3 text-slate-600 2xl:table-cell">
                      {a.category ?? '—'}
                    </td>
                    <td className="hidden px-4 py-3 sm:table-cell">
                      <TriggerTypeBadge type={a.triggerType} />
                    </td>
                    <td className="hidden px-4 py-3 md:table-cell">
                      <StatusBadge status={a.status} />
                    </td>
                    <td className="hidden px-4 py-3 text-slate-600 xl:table-cell">
                      {a.estimatedMinutesSavedPerRun != null
                        ? `${a.estimatedMinutesSavedPerRun} min`
                        : '—'}
                    </td>
                    <td className="hidden px-4 py-3 text-right sm:table-cell">
                      <AutomationActionButton automation={a} onRun={setRunTarget} />
                    </td>
                    <td className="py-3 pl-1 pr-3 text-right sm:px-4">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/automations/${a.id}`}
                          className="text-xs font-medium text-brand hover:underline"
                        >
                          Détails
                        </Link>
                        <ActionsMenu
                          label={`Autres actions pour ${a.name}`}
                          items={actionItems(a)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                {list.data && rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={isPlatform ? 9 : 8}
                      className="px-4 py-8 text-center text-slate-400"
                    >
                      {!filtered ? t('automations.empty') : t('automations.emptyFiltered')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
